import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { IonPage, IonContent, useIonRouter } from '@ionic/react';
import { DataTable } from '../../components/shared/DataTable';
import { QuizQuestionModal } from '../../components/content/QuizQuestionModal';
import { CourseModal } from '../../components/content/CourseModal';
import { CourseVideosModal } from '../../components/content/CourseVideosModal';
import { GameManagerModal } from '../../components/content/GameManagerModal';
import { ConfirmDeleteModal } from '../../components/content/ConfirmDeleteModal';
import { QuizPassFailSettings } from '../../components/content/QuizPassFailSettings';
import { getCourseById, deleteCourse } from '../../services/content/courseService';
import { getQuizQuestionsByCourseId, deleteQuizQuestion } from '../../services/content/quizQuestionService';
import { useBreadcrumbStore } from '../../store/useBreadcrumbStore';
import { useProfileStore } from '../../store/useProfileStore';
import { isContentAdmin } from '../../types/profile';
import type { Course } from '../../types/content/course';
import type { QuizQuestion } from '../../types/content/quizQuestion';
import type { Column } from '../../components/shared/DataTable';

const quizColumns: Column<QuizQuestion>[] = [
  {
    key: 'number',
    label: '#',
    render: (item) => String(item.number),
  },
  { key: 'type', label: 'Type' },
  {
    key: 'text',
    label: 'Question',
    render: (item) => (
      <span className="truncate max-w-[300px] block">{item.text}</span>
    ),
  },
  { key: 'correctOption', label: 'Correct' },
  {
    key: 'options',
    label: 'Options',
    render: (item) => `${item.options?.length ?? 0}`,
  },
];

function QuizRowMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100 transition-colors">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[100px]">
          <button onClick={() => { setOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Edit</button>
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete</button>
        </div>
      )}
    </div>
  );
}

function PageMenu({ onEdit, onVideos, onGame, onDelete }: { onEdit: () => void; onVideos: () => void; onGame: () => void; onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
        </svg>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[120px]">
          <button onClick={() => { setOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Edit</button>
          <button onClick={() => { setOpen(false); onVideos(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Videos</button>
          <button onClick={() => { setOpen(false); onGame(); }} className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50">Game</button>
          <button onClick={() => { setOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50">Delete</button>
        </div>
      )}
    </div>
  );
}

export default function CourseDetailPage() {
  const location = useLocation();
  const router = useIonRouter();
  const params = new URLSearchParams(location.search);
  const courseId = params.get('courseId') || undefined;
  const setBreadcrumb = useBreadcrumbStore((s) => s.set);
  const activeProfile = useProfileStore((s) => s.activeProfile);
  const canEditQuizSettings = isContentAdmin(activeProfile);

  const [course, setCourse] = useState<Course | null>(null);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(true);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [modalQuestion, setModalQuestion] = useState<QuizQuestion | undefined>(undefined);

  // Page-level actions
  const [showEditModal, setShowEditModal] = useState(false);
  const [showVideosModal, setShowVideosModal] = useState(false);
  const [showGameModal, setShowGameModal] = useState(false);
  const [pendingDeleteQuestionId, setPendingDeleteQuestionId] = useState<string | null>(null);
  const [showDeleteCourseConfirm, setShowDeleteCourseConfirm] = useState(false);

  const fetchCourse = useCallback(() => {
    if (!courseId) return;
    getCourseById(courseId, 'quizQuestions,game')
      .then(setCourse)
      .catch(() => {});
  }, [courseId]);

  useEffect(() => { fetchCourse(); }, [fetchCourse]);

  useEffect(() => {
    if (!course) return;
    setBreadcrumb([
      { label: 'Content', path: '/content' },
      { label: 'Courses', path: '/content/courses' },
      { label: course.name },
    ]);
  }, [course, setBreadcrumb]);

  const fetchQuestions = useCallback(async () => {
    if (!courseId) return;
    setQuestionsLoading(true);
    try {
      const results = await getQuizQuestionsByCourseId(courseId);
      results.sort((a, b) => a.number - b.number);
      setQuestions(results);
    } finally {
      setQuestionsLoading(false);
    }
  }, [courseId]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  async function handleDeleteQuestion(id: string) {
    await deleteQuizQuestion(id);
    setPendingDeleteQuestionId(null);
    fetchQuestions();
  }

  async function handleDeleteCourse() {
    if (!courseId) return;
    await deleteCourse(courseId);
    setShowDeleteCourseConfirm(false);
    router.push('/content/courses', 'back');
  }

  if (!courseId) {
    return (
      <IonPage>
        <IonContent>
          <div className="p-8 text-gray-500">No course selected.</div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonContent>
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Top bar: back + page menu */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => router.push('/content/courses', 'back')} className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center gap-1">
              &larr; Back to Courses
            </button>
            {course && (
              <PageMenu
                onEdit={() => setShowEditModal(true)}
                onVideos={() => setShowVideosModal(true)}
                onGame={() => setShowGameModal(true)}
                onDelete={() => setShowDeleteCourseConfirm(true)}
              />
            )}
          </div>

          {course && (
            <div className="mb-6">
              {/* Course header */}
              <div className="flex items-start gap-4 mb-4">
                {course.imageUrl && (
                  <img src={course.imageUrl} alt={course.name} className="w-24 h-16 object-cover rounded-lg border border-gray-200" />
                )}
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{course.name}</h1>
                  <div className="flex items-center gap-3 mt-1">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                      course.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                    }`}>
                      {course.status}
                    </span>
                    <span className="text-sm text-gray-400">{course.steps} steps</span>
                  </div>
                  {course.description && (
                    <p className="text-sm text-gray-600 mt-2 max-w-2xl">{course.description}</p>
                  )}
                </div>
              </div>

              {/* Course info cards */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">Videos</div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-gray-900">{course.videos?.length ?? 0}</span>
                    {course.videos && course.videos.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {course.videos.map((video, i) => (
                          <a
                            key={i}
                            href={video.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            title={video.title}
                          >
                            ({video.order})
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">Quiz</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {course.quizTitle || (course.quizId ? 'Yes' : 'None')}
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="text-xs font-medium text-gray-500 uppercase mb-1">Game</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {course.gameTitle || (course.gameId ? 'Yes' : 'None')}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quiz Pass/Fail Settings */}
          {courseId && (
            <QuizPassFailSettings
              courseId={courseId}
              quizId={course?.quizId}
              canEdit={canEditQuizSettings}
              onSaved={fetchCourse}
            />
          )}

          {/* Quiz Questions */}
          <DataTable
            title="Quiz Questions"
            columns={quizColumns}
            data={questions}
            page={1}
            pageSize={100}
            loading={questionsLoading}
            onPageChange={() => {}}
            onPageSizeChange={() => {}}
            onRefresh={fetchQuestions}
            onAdd={() => { setModalQuestion(undefined); setShowQuizModal(true); }}
            alignTop
            rowMenu={(item) => (
              <QuizRowMenu
                onEdit={() => { setModalQuestion(item); setShowQuizModal(true); }}
                onDelete={() => setPendingDeleteQuestionId(item.id)}
              />
            )}
          />

          {/* Delete Confirmations */}
          {pendingDeleteQuestionId && (
            <ConfirmDeleteModal title="Delete Quiz Question" message="Are you sure you want to delete this quiz question?" onCancel={() => setPendingDeleteQuestionId(null)} onConfirm={() => handleDeleteQuestion(pendingDeleteQuestionId)} />
          )}
          {showDeleteCourseConfirm && (
            <ConfirmDeleteModal title="Delete Course" message="Are you sure you want to delete this course? This will also remove associated quiz questions and game data." onCancel={() => setShowDeleteCourseConfirm(false)} onConfirm={handleDeleteCourse} />
          )}

          {/* Quiz Question Modal */}
          {showQuizModal && courseId && (
            <QuizQuestionModal
              courseId={courseId}
              question={modalQuestion}
              nextNumber={questions.length + 1}
              onClose={() => setShowQuizModal(false)}
              onSuccess={() => { setShowQuizModal(false); fetchQuestions(); }}
            />
          )}

          {/* Edit Course Modal */}
          {showEditModal && course && (
            <CourseModal
              course={course}
              onClose={() => setShowEditModal(false)}
              onSuccess={() => { setShowEditModal(false); fetchCourse(); }}
            />
          )}

          {/* Videos Management Modal */}
          {showVideosModal && course && (
            <CourseVideosModal
              course={course}
              onClose={() => setShowVideosModal(false)}
              onSuccess={() => { setShowVideosModal(false); fetchCourse(); }}
            />
          )}

          {/* Game Management Modal */}
          {showGameModal && courseId && (
            <GameManagerModal
              courseId={courseId}
              onClose={() => setShowGameModal(false)}
            />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
